import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
  Container,
  Paper,
} from '@mui/material';
import { Snackbar } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import config from '../../../config';

const PaymentWaitingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subscriptionId, invoiceId } = useParams();
  const iframeRef = useRef(null);

  const [timeRemaining, setTimeRemaining] = useState(25);
  const [status, setStatus] = useState('payment'); // payment, waiting, success, failed
  const [invoice, setInvoice] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [paymentStarted, setPaymentStarted] = useState(false);
  const [isPlanChange, setIsPlanChange] = useState(false);
  const [planChangeData, setPlanChangeData] = useState(null);
  const [isTokenPurchase, setIsTokenPurchase] = useState(false);
  const [tokenPurchaseData, setTokenPurchaseData] = useState(null);

  // For token purchases: intentId is passed in params, invoiceId is the second param
  // For subscriptions: subscriptionId is passed in params, invoiceId is the second param
  const intentId = isTokenPurchase ? subscriptionId : null;
  const actualInvoiceId = invoiceId;

  const API_BASE = config.API_BASE_V2_URL;
  const CLIENT_ID = config.clientId;

  // Fetch invoice details
  const fetchInvoice = async () => {
    try {
      const response = await fetch(`${API_BASE}/invoices/${invoiceId}`, {
        headers: config.getHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setInvoice(data.invoice);
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
    }
  };

  // Fetch subscription details
  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API_BASE}/subscriptions/${subscriptionId}`, {
        headers: config.getHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setSubscription(data.data);
        return data.data;
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
    return null;
  };

  // Generate PDF on-the-fly
  const generatePdfOnDemand = async () => {
    if (isGeneratingPdf) return;
    setIsGeneratingPdf(true);
    try {
      const response = await fetch(`${API_BASE}/invoices/${invoiceId}/generate-pdf`, {
        method: 'POST',
        headers: config.getHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        await fetchInvoice();
        return true;
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPdf(false);
    }
    return false;
  };

  // Check payment status and handle success
  const checkPaymentStatus = async () => {
    // For token purchases: check payment_intent status using intentId
    if (isTokenPurchase && tokenPurchaseData?.intentId) {
      try {
        // Use payment_intent endpoint with intentId to check status
        const response = await fetch(`${API_BASE}/invoices/payment-intents/${tokenPurchaseData.intentId}`, {
          headers: config.getHeaders()
        });
        const data = await response.json();
        console.log('[PaymentWaitingPage] Token purchase payment_intent status:', data);

        if (data.success && data.data) {
          const intentStatus = data.data.status;
          const invoiceStatus = data.data.invoice_status;

          // Check if payment succeeded (either intent or invoice is captured)
          if (intentStatus === 'succeeded' || intentStatus === 'captured' || invoiceStatus === 'captured') {
            console.log('[PaymentWaitingPage] Token purchase succeeded:', data.data);
            // Payment succeeded! Generate PDF
            await generatePdfOnDemand();
            setStatus('success');
            return true;
          }
        }
      } catch (error) {
        console.error('[PaymentWaitingPage] Error checking payment_intent status:', error);
      }
      return false;
    }

    // For subscriptions/plan changes: check subscription status
    const sub = await fetchSubscription();
    if (sub && sub.subscription_status === 'active') {
      // Payment succeeded! Generate PDF
      await generatePdfOnDemand();
      setStatus('success');
      return true;
    }
    return false;
  };

  // Initialize: Set checkout URL from state and fetch invoice
  useEffect(() => {
    const init = async () => {
      console.log('[PaymentWaitingPage] Initializing with subscriptionId:', subscriptionId, 'invoiceId:', invoiceId);

      // Use checkout_url from location state (passed from CreateStore, PlanChange, or TokenPurchase)
      if (location.state?.checkoutUrl) {
        console.log('[PaymentWaitingPage] Using checkout URL from state:', location.state.checkoutUrl);
        setCheckoutUrl(location.state.checkoutUrl);
      } else {
        console.warn('[PaymentWaitingPage] No checkout URL in state');
      }

      // Extract token purchase data if present
      if (location.state?.isTokenPurchase) {
        console.log('[PaymentWaitingPage] Token purchase detected');
        setIsTokenPurchase(true);
        setTokenPurchaseData({
          intentId: location.state.intentId || subscriptionId,
          invoiceId: location.state.invoiceId || invoiceId,
          purchaseId: location.state.purchaseId,
        });
        // Token purchases use shorter timer (30 seconds instead of 120)
        setTimeRemaining(25);
      }

      // Extract plan change data if present
      if (location.state?.isPlanChange) {
        console.log('[PaymentWaitingPage] Plan change detected');
        setIsPlanChange(true);
        setPlanChangeData({
          oldSubscriptionId: location.state.oldSubscriptionId,
          isUpgrade: location.state.isUpgrade,
          isDowngrade: location.state.isDowngrade,
          prorationCents: location.state.prorationCents,
          chargeAmountCents: location.state.chargeAmountCents,
          planAmountCents: location.state.planAmountCents,
        });
      }

      // Fetch invoice details
      try {
        const response = await fetch(`${API_BASE}/invoices/${invoiceId}`);
        const data = await response.json();
        if (data.success && data.invoice) {
          console.log('[PaymentWaitingPage] Invoice data:', data.invoice);
          setInvoice(data.invoice);
        }
      } catch (error) {
        console.error('[PaymentWaitingPage] Error fetching invoice:', error);
      }
    };
    init();
  }, [invoiceId, subscriptionId, API_BASE, location.state]);



  // Listen for iframe close/payment completion
  useEffect(() => {
    if (!paymentStarted) return;

    let elapsedSeconds = 0;

    // Poll for payment status every 5 seconds
    const pollInterval = setInterval(async () => {
      elapsedSeconds += 5;

      // After 30 seconds: trigger reconciliation every 10 seconds
      // if (elapsedSeconds >= 30 && elapsedSeconds % 10 === 0) {
      //   console.log('[PaymentWaitingPage] Triggering reconciliation at', elapsedSeconds, 'seconds');
      //   try {
      //     const response = await fetch(`${API_BASE}/reconcile/sync/fast`, {
      //       method: 'POST',
      //       headers: {
      //         'secret': config.CRON_SECRET,
      //       },
      //     });
      //     const data = await response.json();
      //     console.log('[PaymentWaitingPage] Reconciliation result:', data);
      //   } catch (error) {
      //     console.error('[PaymentWaitingPage] Error triggering reconciliation:', error);
      //   }
      // }

      const success = await checkPaymentStatus();
      if (success) {
        clearInterval(pollInterval);
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [paymentStarted, navigate]);

  // Timer for payment completion (120 seconds for subscriptions, 30 seconds for tokens)
  useEffect(() => {
    if (status !== 'payment') return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        const newTime = prev - 1;

        // Determine when to trigger temporary payment API
        // For token purchases: 5 seconds remaining (25 seconds elapsed)
        // For subscriptions/plan changes: 95 seconds remaining (25 seconds elapsed)
        const triggerTime = isTokenPurchase ? 10 : 10;

        if (newTime === triggerTime) {
          (async () => {
            console.log(`[PaymentWaitingPage] ${triggerTime} seconds remaining - calling temporary payment API`);
            try {
              let endpoint, payload;

              if (isTokenPurchase && tokenPurchaseData) {
                // Token purchase endpoint
                endpoint = `${API_BASE}/invoices/payment/mark-token-purchase-success`;
                payload = {
                  invoice_id: tokenPurchaseData.invoiceId,
                  purchase_id: tokenPurchaseData.purchaseId,
                };
                console.log('[PaymentWaitingPage] Calling token-purchase endpoint:', endpoint, 'with payload:', payload);
              } else if (isPlanChange && planChangeData) {
                // Plan change endpoint
                endpoint = `${API_BASE}/invoices/payment/mark-plan-change-success`;
                payload = {
                  subscription_id: subscriptionId,
                  invoice_id: invoiceId,
                  old_subscription_id: planChangeData.oldSubscriptionId,
                  is_upgrade: planChangeData.isUpgrade,
                  is_downgrade: planChangeData.isDowngrade,
                  proration_cents: planChangeData.prorationCents,
                  charge_amount_cents: planChangeData.chargeAmountCents,
                  plan_amount_cents: planChangeData.planAmountCents,
                };
                console.log('[PaymentWaitingPage] Calling plan-change endpoint:', endpoint);
              } else {
                // Store creation endpoint
                endpoint = `${API_BASE}/invoices/payment/mark-payment-success`;
                payload = {
                  subscription_id: subscriptionId,
                  invoice_id: invoiceId,
                };
                console.log('[PaymentWaitingPage] Calling store-creation endpoint:', endpoint);
              }

              const response = await fetch(endpoint, {
                method: 'POST',
                headers: config.getHeaders(),
                body: JSON.stringify(payload)
              });
              const data = await response.json();
              console.log('[PaymentWaitingPage] Temporary payment API result:', data);
            } catch (error) {
              console.error('[PaymentWaitingPage] Error calling temporary payment API:', error);
            }

            const success = await checkPaymentStatus();
            if (success) {
              clearInterval(timer);
            }
          })();
        }

        // If still pending at 0 seconds, show failed page
        if (newTime <= 0) {
          clearInterval(timer);
          (async () => {
            const success = await checkPaymentStatus();
            if (!success) {
              setStatus('failed');
            }
          })();
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status, navigate, isTokenPurchase, tokenPurchaseData, isPlanChange, planChangeData, API_BASE, invoiceId, subscriptionId]);

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      {/* LOADING STATE - Waiting for checkout URL */}
      {status === 'payment' && !checkoutUrl && (
        <Card elevation={3}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Loading Payment Form
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Please wait while we prepare your checkout...
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* PAYMENT IFRAME SECTION - WITH TIMER ON SIDE */}
      {status === 'payment' && checkoutUrl && (
        <Card elevation={0} sx={{ borderRadius: 0, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2.5, bgcolor: '#000', color: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CreditCardIcon sx={{ fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }} color="#fff">Complete Your Payment</Typography>
                </Box>
                {/* Timer on the right side of header */}
                <Box sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  px: 2,
                  py: 0.75,
                  borderRadius: '4px',
                  textAlign: 'right'
                }}>
                  <Typography variant="caption" sx={{ color: '#999', display: 'block', fontSize: '0.75rem' }}>Time remaining</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#fff' }}>
                    {timeRemaining}s
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ p: 2 }}>
              {/* Warning Message - AT TOP */}
              <Box sx={{
                bgcolor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                mb: 2
              }}>
                <Typography variant="body2" sx={{ color: '#856404', fontWeight: 500 }}>
                  <strong>Do not close this page</strong> until payment is completed.<br />
                  You'll be redirected once payment is confirmed.
                </Typography>
              </Box>

              {/* Sidglo Checkout Iframe */}
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'hidden',
                  mb: 2,
                  minHeight: '600px',
                  bgcolor: '#f5f5f5',
                }}
              >
                {!paymentStarted && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '600px' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress sx={{ mb: 2 }} />
                      <Typography variant="body2" color="textSecondary">
                        Loading payment form...
                      </Typography>
                    </Box>
                  </Box>
                )}
                <iframe
                  ref={iframeRef}
                  src={checkoutUrl}
                  style={{
                    width: '100%',
                    height: '600px',
                    border: 'none',
                    display: paymentStarted ? 'block' : 'none',
                  }}
                  title="Payment Checkout"
                  onLoad={() => {
                    console.log('[PaymentWaitingPage] Iframe loaded');
                    setPaymentStarted(true);
                  }}
                  onError={() => {
                    console.error('[PaymentWaitingPage] Iframe failed to load');
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}



      {/* SUCCESS STATE - CHECKOUT THEME */}
      {status === 'success' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', bgcolor: '#f5f5f5', p: 2 }}>
          <Card elevation={0} sx={{ maxWidth: 600, width: '100%', borderRadius: 0, bgcolor: 'white' }}>
            {/* Header */}
            <Box sx={{ bgcolor: '#000', color: '#fff', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }} color="#fff">Payment Successful</Typography>
              </Box>
            </Box>

            <CardContent sx={{ p: 4 }}>
              {/* Subtitle */}
              <Typography variant="body1" sx={{ color: '#666', mb: 4, textAlign: 'center' }}>
                Your subscription is now active and ready to use.
              </Typography>

              {/* Invoice Section - Checkout Style */}
              {isGeneratingPdf ? (
                <Box sx={{ mb: 4, p: 3, bgcolor: '#f9f9f9', borderRadius: 1, textAlign: 'center' }}>
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="body2" color="textSecondary">
                    Generating your invoice...
                  </Typography>
                </Box>
              ) : invoice ? (
                <Box sx={{
                  mb: 4,
                  p: 4,
                  bgcolor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}>
                  {/* Company Header */}
                  <Box sx={{ mb: 4, pb: 4, borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', mb: 1 }}>
                      Spokes Digital
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                      2603 Camino Ramon<br />
                      San Ramon, CA 94583<br />
                      (415) 841-2685
                    </Typography>
                  </Box>

                  {/* Invoice Number & Date */}
                  <Box sx={{ mb: 4, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Invoice Number
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#000', fontWeight: 600 }}>
                        #{invoice.invoice_number}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Invoice Date
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#000', fontWeight: 600 }}>
                        {invoice.issued_at ? new Date(invoice.issued_at).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Amount Section - Prominent */}
                  <Box sx={{ mb: 4, pb: 4, borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 1, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Total Amount Due
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#000', fontSize: '2.5rem' }}>
                      ${(invoice.amount).toFixed(2)}
                    </Typography>
                  </Box>

                  {/* Invoice Details Grid */}
                  <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Status
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#000', fontWeight: 500 }}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Description
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#000', fontWeight: 500 }}>
                        {invoice.description || 'Subscription Payment'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Download Button */}
                  {invoice.pdf_url ? (
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: '#000',
                        color: '#fff',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        py: 1.5,
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        letterSpacing: '0.5px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: '#333',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        },
                      }}
                      onClick={() => {
                        window.open(invoice.pdf_url, '_blank');
                      }}
                      fullWidth
                    >
                      ⬇ Download Invoice PDF
                    </Button>
                  ) : null}
                </Box>
              ) : null}

              {/* Action Button */}
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#000',
                  color: '#fff',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  py: 1.75,
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  letterSpacing: '0.5px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: '#333',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  },
                }}
                onClick={() => navigate('/store-management')}
                fullWidth
              >
                Back to Store Management
              </Button>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* FAILED STATE - CHECKOUT THEME */}
      {status === 'failed' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', bgcolor: '#f5f5f5', p: 2 }}>
          <Card elevation={0} sx={{ maxWidth: 600, width: '100%', borderRadius: 0, bgcolor: 'white' }}>
            {/* Header */}
            <Box sx={{ bgcolor: '#000', color: '#fff', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon sx={{ fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Payment Not Confirmed</Typography>
              </Box>
            </Box>

            <CardContent sx={{ p: 4 }}>
              {/* Message */}
              <Typography variant="body1" sx={{ color: '#666', mb: 4, textAlign: 'center' }}>
                We didn't receive payment confirmation within the timeout period. Please try again.
              </Typography>

              {/* Invoice Section - Modern Clean Template */}
              {invoice && (
                <Box sx={{
                  mb: 4,
                  p: 4,
                  bgcolor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}>
                  {/* Company Header */}
                  <Box sx={{ mb: 4, pb: 4, borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', mb: 1 }}>
                      Spokes Digital
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                      2603 Camino Ramon<br />
                      San Ramon, CA 94583<br />
                      (415) 841-2685
                    </Typography>
                  </Box>

                  {/* Invoice Number & Date */}
                  <Box sx={{ mb: 4, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Invoice Number
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#000', fontWeight: 600 }}>
                        #{invoice.invoice_number || 'Pending'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Invoice Date
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#000', fontWeight: 600 }}>
                        {invoice.issued_at ? new Date(invoice.issued_at).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Amount Section - Prominent */}
                  <Box sx={{ mb: 4, pb: 4, borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 1, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Total Amount Due
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#000', fontSize: '2.5rem' }}>
                      ${(invoice.amount).toFixed(2)}
                    </Typography>
                  </Box>

                  {/* Invoice Details Grid */}
                  <Box sx={{ mb: 4, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Status
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#000', fontWeight: 500 }}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Description
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#000', fontWeight: 500 }}>
                        {invoice.description || 'Subscription Payment'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Retry Payment Button - Re-open iframe with checkout URL */}
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: '#000',
                      color: '#fff',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      letterSpacing: '0.5px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: '#333',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      },
                    }}
                    onClick={() => {
                      // Re-open iframe with the checkout URL
                      if (checkoutUrl) {
                        setStatus('payment');
                        setTimeRemaining(25);
                        setPaymentStarted(false);
                      } else if (invoice.payment_link) {
                        // Fallback: Use payment_link from invoice
                        setCheckoutUrl(invoice.payment_link);
                        setStatus('payment');
                        setTimeRemaining(25);
                        setPaymentStarted(false);
                      } else {
                        // Last resort: Fetch fresh invoice data
                        fetchInvoice();
                      }
                    }}
                    fullWidth
                  >
                    ↻ Retry Payment
                  </Button>
                </Box>
              )}

              {/* Action Button */}
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#000',
                  color: '#fff',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  py: 1.75,
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  letterSpacing: '0.5px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: '#333',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  },
                }}
                onClick={() => navigate('/store-management')}
                fullWidth
              >
                Back to Store Management
              </Button>
            </CardContent>
          </Card>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default PaymentWaitingPage;

